<template>
  <section class="map-template-library">
    <page-header title="网格基础资源管理" description="统一维护基础网格地图、网格人员档案和岗位信息，供网格案例选用。选择地图模板的“详情”进入基础网格地图管理，已发布版本需复制为草稿后修改。">
      <template #actions>
        <a class="btn" href="map-template-guide.html" target="_blank" rel="noopener">操作说明书</a>
        <router-link class="btn primary" :to="detailRoute('new')">新建模板</router-link>
      </template>
    </page-header>
    <network-resource-nav />
    <operation-message :value="message" type="error" @consumed="message = ''" />
    <div class="panel">
      <div class="panel-title">基础网格地图列表 <small>{{ loading ? '加载中…' : '共 ' + items.length + ' 个模板' }}</small></div>
      <form class="filter-bar" @submit.prevent="load">
        <input v-model.trim="keyword" class="filter-input" aria-label="搜索地图名称或编码" placeholder="搜索地图名称 / 编码">
        <select v-model="status" class="field" aria-label="地图模板状态"><option value="">全部状态</option><option value="DRAFT">草稿</option><option value="PUBLISHED">已发布</option><option value="DISABLED">已停用</option><option value="ARCHIVED">已归档</option></select>
        <button class="btn" type="submit" :disabled="loading">查询</button>
        <button class="btn" type="button" :disabled="loading" @click="load">刷新列表</button>
      </form>
      <div class="table-wrap" :aria-busy="loading ? 'true' : 'false'">
        <table class="map-template-table">
          <thead><tr><th>模板名称</th><th>版本</th><th>底图单元</th><th>图层</th><th>参考网格</th><th>部件点位</th><th>状态</th><th>更新时间</th><th>操作</th></tr></thead>
          <tbody><tr v-for="item in visibleItems" :key="item.id" :data-template-id="item.id">
            <td><strong>{{ item.name }}</strong><div v-if="item.code" class="muted">{{ item.code }}</div></td>
            <td>v{{ item.versionNo || 1 }}</td>
            <td>{{ count(item.cells) }} 个<div class="muted">{{ item.rows }} 行 × {{ item.cols }} 列</div></td>
            <td>{{ count(item.layers) }}</td><td>{{ count(item.gridSchemes) }}</td><td>{{ count(item.partPoints) }}</td>
            <td><status-tag :value="item.status" :label="statusLabel(item.status)" /></td>
            <td class="map-template-time">{{ time(item.updatedAt || item.createdAt) }}</td>
            <td><router-link class="btn small" :to="detailRoute(item.id)">详情</router-link></td>
          </tr></tbody>
        </table>
        <div v-if="loading" class="empty" role="status">正在加载地图模板…</div>
        <div v-else-if="!items.length" class="empty">{{ loadFailed ? '地图模板加载失败，请重试' : '暂无符合条件的地图模板，可调整筛选或新建模板' }}</div>
        <list-pagination v-if="!loading && items.length" :page="currentPage" :page-size="pageSize" :total="items.length" @change="currentPage = $event" />
      </div>
    </div>
  </section>
</template>

<script>
import PageHeader from '../components/PageHeader.vue'
import StatusTag from '../components/StatusTag.vue'
import NetworkResourceNav from '../components/NetworkResourceNav.vue'
import ListPagination from '../components/ListPagination.vue'
import { api, errorText } from '../services/api'

export default {
  name: 'MapTemplateLibrary', components: { PageHeader, StatusTag, NetworkResourceNav, ListPagination },
  data () { return { items: [], keyword: '', status: '', currentPage: 1, pageSize: 10, loading: false, loadFailed: false, message: '', loadSequence: 0 } },
  computed: {
    visibleItems () { const start = (this.currentPage - 1) * this.pageSize; return this.items.slice(start, start + this.pageSize) }
  },
  watch: {
    keyword () { this.currentPage = 1 },
    status () { this.currentPage = 1 },
    items () { this.ensurePage() }
  },
  created () { this.load() },
  methods: {
    async load () {
      const sequence = ++this.loadSequence
      this.loading = true; this.loadFailed = false; this.message = ''
      try {
        const items = await api.list('/network/map-templates', { keyword: this.keyword, status: this.status })
        if (!this._isDestroyed && sequence === this.loadSequence) { this.items = items || []; this.ensurePage() }
      } catch (error) {
        if (!this._isDestroyed && sequence === this.loadSequence) { this.items = []; this.loadFailed = true; this.message = errorText(error) }
      } finally {
        if (!this._isDestroyed && sequence === this.loadSequence) this.loading = false
      }
    },
    ensurePage () { const totalPages = Math.max(1, Math.ceil(this.items.length / this.pageSize)); if (this.currentPage > totalPages) this.currentPage = totalPages },
    detailRoute (id) { return { name: 'map-template-detail', params: { templateId: id } } },
    count (items) { return Array.isArray(items) ? items.length : 0 },
    statusLabel (value) { return { DRAFT: '草稿', PUBLISHED: '已发布', DISABLED: '已停用', ARCHIVED: '已归档' }[value] || value || '—' },
    time (value) {
      if (value == null || value === '') return '—'
      if (!/^-?\d+(\.\d+)?$/.test(String(value))) return String(value).replace('T', ' ').slice(0, 19)
      const number = Number(value)
      const date = new Date(Math.abs(number) < 1e11 ? number * 1000 : number)
      if (!Number.isFinite(date.getTime())) return '—'
      const parts = new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).formatToParts(date)
      const part = key => parts.find(item => item.type === key).value
      return part('year') + '-' + part('month') + '-' + part('day') + ' ' + part('hour') + ':' + part('minute') + ':' + part('second')
    }
  }
}
</script>

